import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";

type Tab = "privacy" | "terms" | "cookies" | "accessibility";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "privacy",       label: "Privacy Policy",   icon: "ri-shield-user-line"  },
  { id: "terms",         label: "Terms of Service", icon: "ri-file-text-line"    },
  { id: "cookies",       label: "Cookie Policy",    icon: "ri-list-settings-line" },
  { id: "accessibility", label: "Accessibility",    icon: "ri-eye-line"          },
];

function PrivacyContent() {
  return (
    <article className="prose-custom">
      <p className="text-slate-500 text-sm mb-8">Last updated: March 15, 2026</p>

      <Section title="1. Introduction">
        <p>Gig <em>n&apos;</em> Cash Inc. (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your data when you use our platform at gignandcash.com (the &quot;Platform&quot;).</p>
      </Section>

      <Section title="2. Information We Collect">
        <p>We collect information you provide directly, such as:</p>
        <ul>
          <li>Account registration details (name, email address, password)</li>
          <li>Profile information (work history, skills, capabilities)</li>
          <li>Payment and billing information (processed securely via Stripe)</li>
          <li>Communications you send through the Platform</li>
          <li>Job postings and applications</li>
        </ul>
        <p>We also collect data automatically when you use our Platform, including IP address, browser type, device identifiers, pages visited, and usage patterns.</p>
      </Section>

      <Section title="3. How We Use Your Information">
        <ul>
          <li>To provide, maintain, and improve the Platform</li>
          <li>To match workers with relevant job opportunities</li>
          <li>To process payments and prevent fraud</li>
          <li>To send transactional and marketing communications (with your consent)</li>
          <li>To comply with legal obligations</li>
        </ul>
      </Section>

      <Section title="4. Sharing Your Information">
        <p>We do not sell your personal data. We may share it with:</p>
        <ul>
          <li><strong>Employers / Workers</strong> — limited profile data necessary to facilitate a job match</li>
          <li><strong>Service providers</strong> — payment processors, cloud hosting, analytics tools</li>
          <li><strong>Legal authorities</strong> — when required by law or to protect our rights</li>
        </ul>
      </Section>

      <Section title="5. Data Retention">
        <p>We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting support.</p>
      </Section>

      <Section title="6. Your Rights">
        <p>Depending on your location, you may have rights including: access, correction, deletion, portability, and objection to processing. Contact us at <strong>privacy@gignandcash.com</strong> to exercise these rights.</p>
      </Section>

      <Section title="7. Security">
        <p>We employ 256-bit SSL encryption, regular security audits, and strict access controls. However, no method of transmission over the internet is 100% secure.</p>
      </Section>

      <Section title="8. Contact Us">
        <p>For privacy-related questions, reach us at <strong>privacy@gignandcash.com</strong> or write to: Gig <em>n&apos;</em> Cash Inc., 340 Pine Street, Suite 800, San Francisco, CA 94104.</p>
      </Section>
    </article>
  );
}

function TermsContent() {
  return (
    <article className="prose-custom">
      <p className="text-slate-500 text-sm mb-8">Last updated: March 15, 2026</p>

      <Section title="1. Acceptance of Terms">
        <p>By creating an account or using the Gig <em>n&apos;</em> Cash platform, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Platform.</p>
      </Section>

      <Section title="2. Eligibility">
        <p>You must be at least 18 years old and legally permitted to work in your jurisdiction to use this Platform. By using the Platform, you represent and warrant that you meet these requirements.</p>
      </Section>

      <Section title="3. Worker Accounts">
        <ul>
          <li>Workers may create profiles showcasing their capabilities and apply to jobs</li>
          <li>Workers are independent contractors, not employees of Gig <em>n&apos;</em> Cash</li>
          <li>Workers are responsible for their own taxes and legal obligations</li>
          <li>Misrepresentation of skills or experience may result in account termination</li>
        </ul>
      </Section>

      <Section title="4. Employer Accounts">
        <ul>
          <li>Employers may post jobs, review applicants, and hire workers through the Platform</li>
          <li>Job postings must be accurate, lawful, and comply with applicable employment regulations</li>
          <li>Employers are responsible for verifying worker credentials as needed</li>
        </ul>
      </Section>

      <Section title="5. Payments & Fees">
        <p>Subscription fees are billed in advance on a monthly or annual basis. Refunds are available within 7 days of initial purchase for annual plans. Monthly plans are non-refundable. We reserve the right to change pricing with 30 days&apos; notice.</p>
      </Section>

      <Section title="6. Prohibited Conduct">
        <ul>
          <li>Posting false, misleading, or fraudulent content</li>
          <li>Harassing, threatening, or discriminating against other users</li>
          <li>Circumventing the Platform to avoid fees</li>
          <li>Reverse engineering or scraping the Platform</li>
        </ul>
      </Section>

      <Section title="7. Termination">
        <p>We may suspend or terminate your account at our discretion for violations of these Terms. You may terminate your account at any time through your account settings.</p>
      </Section>

      <Section title="8. Disclaimer & Limitation of Liability">
        <p>The Platform is provided &quot;as is&quot; without warranties of any kind. To the maximum extent permitted by law, Gig <em>n&apos;</em> Cash shall not be liable for indirect, incidental, or consequential damages arising from your use of the Platform.</p>
      </Section>
    </article>
  );
}

function CookiesContent() {
  return (
    <article className="prose-custom">
      <p className="text-slate-500 text-sm mb-8">Last updated: March 15, 2026</p>

      <Section title="1. What Are Cookies?">
        <p>Cookies are small text files stored on your device when you visit our Platform. They help us remember your preferences, keep you signed in, and understand how you use our services.</p>
      </Section>

      <Section title="2. Types of Cookies We Use">
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="font-bold text-slate-800 mb-1">Essential Cookies</h4>
            <p className="text-slate-600 text-sm">Required for the Platform to function. Includes session management, authentication tokens, and security cookies. Cannot be disabled.</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="font-bold text-slate-800 mb-1">Analytics Cookies</h4>
            <p className="text-slate-600 text-sm">Help us understand Platform usage through anonymized data (e.g., pages visited, time on site). Used to improve user experience.</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="font-bold text-slate-800 mb-1">Preference Cookies</h4>
            <p className="text-slate-600 text-sm">Remember your settings such as language preference, saved searches, and dashboard layout.</p>
          </div>
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
            <h4 className="font-bold text-slate-800 mb-1">Marketing Cookies</h4>
            <p className="text-slate-600 text-sm">Used to show relevant advertisements and measure campaign effectiveness. You may opt out of these in your account settings.</p>
          </div>
        </div>
      </Section>

      <Section title="3. Managing Cookies">
        <p>You can control cookies through your browser settings. Note that disabling essential cookies may prevent parts of the Platform from functioning correctly.</p>
      </Section>

      <Section title="4. Third-Party Cookies">
        <p>Some cookies are placed by third-party services we use (e.g., Stripe for payments, analytics providers). These are governed by the respective third parties&apos; privacy policies.</p>
      </Section>
    </article>
  );
}

function AccessibilityContent() {
  return (
    <article className="prose-custom">
      <p className="text-slate-500 text-sm mb-8">Last updated: March 15, 2026</p>

      <Section title="Our Commitment">
        <p>Gig <em>n&apos;</em> Cash is committed to ensuring digital accessibility for people with disabilities. We continually improve the user experience for everyone and apply relevant accessibility standards.</p>
      </Section>

      <Section title="Standards We Follow">
        <p>We aim to conform to the <strong>Web Content Accessibility Guidelines (WCAG) 2.1 Level AA</strong>. These guidelines explain how to make web content more accessible to people with disabilities.</p>
      </Section>

      <Section title="Features We Provide">
        <ul>
          <li>Full keyboard navigation support throughout the Platform</li>
          <li>Sufficient color contrast ratios for all text and UI elements</li>
          <li>ARIA labels and semantic HTML for screen reader compatibility</li>
          <li>Resizable text without loss of content or functionality</li>
          <li>Alt text for all meaningful images</li>
          <li>Focus indicators on all interactive elements</li>
          <li>No content that flashes more than 3 times per second</li>
        </ul>
      </Section>

      <Section title="Known Limitations">
        <p>While we strive for full accessibility, some areas of the Platform are still being improved. We are actively working to address any gaps and welcome your feedback.</p>
      </Section>

      <Section title="Feedback & Contact">
        <p>If you experience any accessibility barriers on our Platform, please contact us:</p>
        <ul>
          <li>Email: <strong>accessibility@gignandcash.com</strong></li>
          <li>Phone: +1 (800) 555-0199 (Monday–Friday, 9am–5pm PT)</li>
        </ul>
        <p>We aim to respond to accessibility feedback within 2 business days.</p>
      </Section>

      <Section title="Formal Complaints">
        <p>If you are not satisfied with our response, you have the right to contact your local accessibility enforcement authority or ombudsman.</p>
      </Section>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8">
      <h3 className="text-lg font-bold text-slate-900 mb-3 pb-2 border-b border-slate-100">{title}</h3>
      <div className="text-slate-600 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

const CONTENT_MAP: Record<Tab, () => JSX.Element> = {
  privacy:       PrivacyContent,
  terms:         TermsContent,
  cookies:       CookiesContent,
  accessibility: AccessibilityContent,
};

export default function LegalPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as Tab | null;
  const [activeTab, setActiveTab] = useState<Tab>(
    tabParam && TABS.some((t) => t.id === tabParam) ? tabParam : "privacy"
  );

  useEffect(() => {
    const t = searchParams.get("tab") as Tab | null;
    if (t && TABS.some((tab) => tab.id === t)) {
      setActiveTab(t);
    }
  }, [searchParams]);

  const handleTab = (tab: Tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const ActiveContent = CONTENT_MAP[activeTab];
  const activeTabMeta = TABS.find((t) => t.id === activeTab)!;

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      {/* Hero */}
      <div className="bg-slate-900 pt-24 pb-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-shield-check-line text-emerald-400 text-sm" />
            <span className="text-emerald-400 text-xs font-semibold uppercase tracking-wider">Legal &amp; Policies</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-3">{activeTabMeta.label}</h1>
          <p className="text-slate-400 text-sm max-w-lg mx-auto">
            We believe in transparency. Read our policies to understand how we handle your data and what governs our platform.
          </p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-1 py-2 scrollbar-none">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                }`}
              >
                <i className={`${tab.icon} text-base`} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <ActiveContent />
          </div>

          {/* Sidebar */}
          <aside className="lg:w-64 shrink-0">
            <div className="sticky top-24 space-y-4">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-4">Other Policies</h4>
                <ul className="space-y-2">
                  {TABS.filter((t) => t.id !== activeTab).map((tab) => (
                    <li key={tab.id}>
                      <button
                        onClick={() => handleTab(tab.id)}
                        className="flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-600 transition-colors cursor-pointer w-full text-left"
                      >
                        <i className={`${tab.icon} text-slate-400`} />
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                <i className="ri-customer-service-2-line text-2xl text-emerald-600 mb-2 block" />
                <h4 className="font-bold text-slate-800 text-sm mb-1">Need Help?</h4>
                <p className="text-slate-500 text-xs mb-3 leading-relaxed">Have questions about our policies? Our team is happy to clarify.</p>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-800 transition-colors"
                >
                  Contact Support <i className="ri-arrow-right-line" />
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Bottom Nav */}
      <div className="border-t border-slate-100 bg-slate-50 py-8 px-4">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-xs">&copy; {new Date().getFullYear()} Gig <em>n&apos;</em> Cash Inc. All rights reserved.</p>
          <Link to="/" className="text-xs text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
            &larr; Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
